import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, Plus, Trash2, CircleDot } from 'lucide-react';
import {
  emailAdminAPI,
  extractErrorCode,
  extractErrorMessage,
  workspaceAPI,
  EmailSuppression,
  EmailWebhookHealth,
} from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';

const PAGE_SIZE = 25;

interface Message {
  type: 'success' | 'error';
  text: string;
}

type HealthStatus = 'ok' | 'stale' | 'dead' | 'never';

function resolveHealthStatus(h: EmailWebhookHealth | null): HealthStatus {
  if (!h || !h.last_event_received_at) return 'never';
  const last = new Date(h.last_event_received_at).getTime();
  const now = Date.now();
  const delta = now - last;
  if (delta <= 60 * 60 * 1000) return 'ok';
  if (delta <= 24 * 60 * 60 * 1000) return 'stale';
  return 'dead';
}

const STATUS_COPY: Record<HealthStatus, { label: string; dot: string; ring: string; text: string }> = {
  ok:    { label: 'Healthy',  dot: 'bg-green-400',  ring: 'ring-green-400/30', text: 'text-green-300' },
  stale: { label: 'Degraded', dot: 'bg-amber-400',  ring: 'ring-amber-400/30', text: 'text-amber-300' },
  dead:  { label: 'Down',     dot: 'bg-red-400',    ring: 'ring-red-400/30',   text: 'text-red-300' },
  never: { label: 'No events', dot: 'bg-slate-500', ring: 'ring-slate-500/30', text: 'text-slate-300' },
};

function formatRelative(iso: string | null): string {
  if (!iso) return 'never';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'never';
  return d.toLocaleString();
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

const EmailDeliverabilityTab: React.FC = () => {
  const { user } = useAuth();
  const [health, setHealth] = useState<EmailWebhookHealth | null>(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [rows, setRows] = useState<EmailSuppression[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const [message, setMessage] = useState<Message | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Resolve workspace-admin status by finding the current user in the members list —
  // same pattern MembersTab uses (LT-126). Global User.is_admin is a different flag.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const members = await workspaceAPI.listMembers();
        if (cancelled) return;
        const me = members.find((m) => m.id === user.id);
        setIsAdmin(me?.role === 'admin');
      } catch {
        if (!cancelled) setIsAdmin(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const refreshHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const h = await emailAdminAPI.getWebhookHealth();
      setHealth(h);
    } catch {
      setHealth(null);
    } finally {
      setHealthLoading(false);
    }
  }, []);

  const refreshRows = useCallback(async (pageToLoad: number, searchTerm: string) => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await emailAdminAPI.listSuppressions({
        search: searchTerm || undefined,
        limit: PAGE_SIZE,
        offset: pageToLoad * PAGE_SIZE,
      });
      setRows(data);
    } catch (err) {
      setLoadError(extractErrorMessage(err, 'Failed to load suppression list.'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshHealth();
  }, [refreshHealth]);

  useEffect(() => {
    void refreshRows(page, search);
  }, [page, search, refreshRows]);

  useEffect(() => {
    if (message?.type === 'success') {
      const t = setTimeout(() => setMessage(null), 4000);
      return () => clearTimeout(t);
    }
  }, [message]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    setSearch(searchInput.trim());
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = addEmail.trim();
    if (!value) {
      setMessage({ type: 'error', text: 'Email is required.' });
      return;
    }
    setAdding(true);
    try {
      await emailAdminAPI.addSuppression(value);
      setMessage({ type: 'success', text: `Added ${value} to the suppression list.` });
      setAddEmail('');
      setAddOpen(false);
      setPage(0);
      await refreshRows(0, search);
    } catch (err) {
      const code = extractErrorCode(err);
      const text =
        code === 'already_suppressed'
          ? 'This email is already on the suppression list.'
          : extractErrorMessage(err, 'Failed to add suppression.');
      setMessage({ type: 'error', text });
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (row: EmailSuppression) => {
    setRemovingId(row.id);
    try {
      await emailAdminAPI.removeSuppression(row.id);
      setMessage({ type: 'success', text: `Removed ${row.email}.` });
      await refreshRows(page, search);
    } catch (err) {
      setMessage({ type: 'error', text: extractErrorMessage(err, 'Failed to remove suppression.') });
    } finally {
      setRemovingId(null);
    }
  };

  const healthStatus = useMemo(() => resolveHealthStatus(health), [health]);
  const copy = STATUS_COPY[healthStatus];

  return (
    <div className="space-y-6 max-w-5xl">
      {message && (
        <div
          className={`px-3 py-2 rounded-lg text-sm flex items-center justify-between ${
            message.type === 'success'
              ? 'bg-green-500/20 border border-green-400/40 text-green-100'
              : 'bg-red-500/20 border border-red-400/40 text-red-100'
          }`}
        >
          <span>{message.text}</span>
          <button
            type="button"
            onClick={() => setMessage(null)}
            className="ml-2 text-gray-300 hover:text-white text-lg leading-none"
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
      )}

      {/* Webhook health card */}
      <div className="rounded-xl bg-white/5 border border-white/10 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-white">SendGrid webhook</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Monitors inbound delivery, open, click, bounce, and unsubscribe events.
            </p>
          </div>
          <span
            className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs ring-1 ${copy.ring} ${copy.text} bg-white/5`}
          >
            <CircleDot className="w-3 h-3" />
            <span>{copy.label}</span>
          </span>
        </div>
        {healthLoading ? (
          <div className="mt-4 animate-pulse h-10 bg-white/5 rounded" />
        ) : (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-white/5 rounded-lg px-3 py-2">
              <div className="text-xs text-gray-400">Last event received</div>
              <div className="text-sm text-white mt-0.5">
                {formatRelative(health?.last_event_received_at ?? null)}
              </div>
            </div>
            <div className="bg-white/5 rounded-lg px-3 py-2">
              <div className="text-xs text-gray-400">Events in last 24 hours</div>
              <div className="text-sm text-white mt-0.5">{health?.events_last_24h ?? 0}</div>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 mt-3">
          <span className={`w-2 h-2 rounded-full ${copy.dot}`} aria-hidden />
          <span className="text-xs text-gray-400">
            Green = event in last hour • Amber = within 24h • Red = &gt; 24h / never.
          </span>
        </div>
      </div>

      {/* Suppression list */}
      <div className="rounded-xl bg-white/5 border border-white/10">
        <div className="px-4 py-3 border-b border-white/10 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-sm font-medium text-white">Suppression list</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Recipients blocked from outbound sends in this workspace.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <form onSubmit={handleSearchSubmit} className="relative">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search email"
                className="pl-8 pr-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
              />
            </form>
            {isAdmin && (
              <button
                type="button"
                onClick={() => {
                  setAddOpen(true);
                  setAddEmail('');
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
              >
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            )}
          </div>
        </div>

        {!isAdmin && (
          <div className="px-4 py-2 text-xs text-gray-300 bg-white/[0.02]">
            You are a member of this workspace. Contact a workspace admin to edit suppressions.
          </div>
        )}

        {loadError ? (
          <div className="px-4 py-6 text-sm text-red-200 bg-red-500/10">{loadError}</div>
        ) : loading ? (
          <div className="p-4 space-y-2">
            <div className="h-8 bg-white/5 rounded animate-pulse" />
            <div className="h-8 bg-white/5 rounded animate-pulse" />
            <div className="h-8 bg-white/5 rounded animate-pulse" />
          </div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">
            {search
              ? 'No suppressions match that search.'
              : 'No suppressions yet — unsubscribes, hard bounces, and spam reports will show up here automatically.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs text-gray-400 border-b border-white/10">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Email</th>
                  <th className="text-left px-4 py-2 font-medium">Reason</th>
                  <th className="text-left px-4 py-2 font-medium">Suppressed</th>
                  {isAdmin && <th className="px-4 py-2" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-white/[0.02]">
                    <td className="px-4 py-2 text-white">{row.email}</td>
                    <td className="px-4 py-2">
                      <span className="inline-block px-2 py-0.5 text-xs bg-white/5 border border-white/10 rounded text-gray-200">
                        {row.reason}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-300">{formatDate(row.suppressed_at)}</td>
                    {isAdmin && (
                      <td className="px-4 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => void handleRemove(row)}
                          disabled={removingId === row.id}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-300 hover:text-red-200 disabled:opacity-50"
                          aria-label={`Remove ${row.email} from suppression list`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Remove
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="px-4 py-2 flex items-center justify-between text-xs text-gray-400 border-t border-white/10">
          <span>
            Page {page + 1} • Showing {rows.length} row{rows.length === 1 ? '' : 's'}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || loading}
              className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 disabled:opacity-50"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={rows.length < PAGE_SIZE || loading}
              className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {addOpen && isAdmin && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => !adding && setAddOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-xl bg-slate-900 border border-white/10 p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-medium text-white">Add to suppression list</h3>
            <p className="text-xs text-gray-400 mt-1">
              Outbound sends to this address will be blocked in this workspace.
            </p>
            <form onSubmit={handleAdd} className="mt-4 space-y-3">
              <input
                type="email"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                placeholder="name@example.com"
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                autoFocus
              />
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setAddOpen(false)}
                  disabled={adding}
                  className="px-3 py-1.5 text-sm text-gray-300 hover:text-white disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={adding}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  {adding ? 'Adding…' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailDeliverabilityTab;
